import { cn } from '@/lib/utils'
import { StatusAprovacao } from '@acme/core'
import { cva, type VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import React from 'react'

interface IProps {
    status: string
}

export function SimpleChip({ status }: IProps) {
    switch (status) {
        case StatusAprovacao.PENDENTE:
            return <Chip label="Pendente" color="warning" />
        case StatusAprovacao.PENDENTE_EXCLUSAO:
            return <Chip label="Exclusão pendente" color="warning" />
        case StatusAprovacao.APROVADO:
            return <Chip label="Aprovado" color="success" />
        case StatusAprovacao.REPROVADO:
            return <Chip label="Reprovado" color="error" />
        default:
            return <Chip label={status.toString()} color="info" />
    }
}

const chipVariants = cva(
    [
        'inline-flex items-center justify-center gap-1.5 rounded-full',
        'whitespace-nowrap transition-colors duration-200 [&_svg]:size-4',
    ].join(' '),
    {
        variants: {
            color: {
                default: '',
                primary: '',
                secondary: '',
                success: '',
                error: '',
                warning: '',
                info: '',
            },
            variant: {
                filled: 'text-secondary-foreground',
                outlined: 'bg-transparent border',
            },
            size: {
                small: 'px-2 py-0.5 text-xs [&_svg]:size-3.5',
                medium: 'px-3 py-2 text-sm',
            },
        },
        compoundVariants: [
            // filled
            {
                variant: 'filled',
                color: 'default',
                className: 'bg-accent text-foreground',
            },
            {
                variant: 'filled',
                color: 'primary',
                className: 'bg-primary text-primary-foreground',
            },
            {
                variant: 'filled',
                color: 'secondary',
                className: 'bg-secondary',
            },
            { variant: 'filled', color: 'success', className: 'bg-success' },
            { variant: 'filled', color: 'error', className: 'bg-error' },
            { variant: 'filled', color: 'warning', className: 'bg-warning' },
            { variant: 'filled', color: 'info', className: 'bg-info' },
            // outlined
            {
                variant: 'outlined',
                color: 'default',
                className: 'border-border text-foreground',
            },
            {
                variant: 'outlined',
                color: 'primary',
                className: 'border-primary text-primary',
            },
            {
                variant: 'outlined',
                color: 'secondary',
                className: 'border-border text-secondary-foreground',
            },
            {
                variant: 'outlined',
                color: 'success',
                className: 'border-success text-success',
            },
            {
                variant: 'outlined',
                color: 'error',
                className: 'border-error text-error',
            },
            {
                variant: 'outlined',
                color: 'warning',
                className: 'border-warning text-warning',
            },
            {
                variant: 'outlined',
                color: 'info',
                className: 'border-info text-info',
            },
        ],
        defaultVariants: {
            color: 'default',
            variant: 'filled',
            size: 'medium',
        },
    },
)

interface CustomChipProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
        VariantProps<typeof chipVariants> {
    label: React.ReactNode
    /** Ícone exibido antes do label. */
    icon?: React.ReactNode
    /** Quando definido, exibe um botão de remover. */
    onDelete?: (_ignored: any) => any
    /** Ícone customizado do botão de remover. */
    deleteIcon?: React.ReactNode
    disabled?: boolean
}

export const Chip = React.forwardRef<HTMLDivElement, CustomChipProps>(
    function Chip(
        {
            label,
            color,
            variant,
            size,
            icon,
            className,
            onDelete,
            deleteIcon,
            disabled,
            onClick,
            ...props
        },
        ref,
    ) {
        const clickable = !!onClick && !disabled

        return (
            <div
                ref={ref}
                aria-disabled={disabled || undefined}
                onClick={disabled ? undefined : onClick}
                className={cn(
                    chipVariants({ color, variant, size }),
                    clickable && 'cursor-pointer hover:opacity-90',
                    disabled && 'pointer-events-none opacity-disabled',
                    className,
                )}
                {...props}
            >
                {icon}
                {label}
                {!!onDelete && !disabled && (
                    <button
                        type="button"
                        title="Remover"
                        onClick={e => {
                            e.stopPropagation()
                            onDelete(e)
                        }}
                        className={cn(
                            'ml-0.5 -mr-1 flex items-center justify-center rounded-full p-0.5',
                            'hover:cursor-pointer border-none dark:text-primary',
                            'bg-card hover:bg-primary/10 dark:outline dark:outline-primary',
                        )}
                    >
                        {deleteIcon ?? <XIcon className="size-4" />}
                    </button>
                )}
            </div>
        )
    },
)
